import React from 'react';
import {observer} from 'mobx-react';
import {Check} from '@sparkware/app-utils';
import {OrbitComponent} from '../base-components/orbit-component/orbit-component';
import {JACKPOT_STATE, JackpotInternalId, JackpotType} from '../../../common/const/jackpot_map_ids';
import {JackpotWidgetConfiguration} from '../../services/configurations/config-data-sections/jackpot-widget.configuration';
import {JackpotWidgetBox} from './boxes/jackpot-widget.box';
import {JackpotWidgetBgBox} from './boxes/bg/jackpot-widget-bg.box';
import {JackpotWidgetLogoBox} from './boxes/logo/jackpot-widget-logo.box';
import {JackpotWidgetAmountBox} from './boxes/amount/jackpot-widget-amount.box';
import {JackpotWidgetFooterBox} from './boxes/footer/jackpot-widget-footer.box';
import {JackpotWidgetClockLabelBox} from './boxes/footer/clock/label/jackpot-widget-clock-label.box';
import {JackpotWidgetClockComponent} from './jackpot-widget-clock.component';
import {JackpotWidgetAmountLabelBox} from './boxes/amount/label/jackpot-widget-amount-label.box';
import {JackpotWidgetCollectingLabelBox} from './boxes/footer/collecting/label/jackpot-widget-collecting-label';
import {JackpotWidgetCollectingAmountBox} from './boxes/footer/collecting/amount/jackpot-widget-collecting-amount';
import {JackpotWidgetBorderShine, JackpotWidgetRadialShine} from './boxes/shine/jackpot-widget-shine.box';
import {JackpotWidgetAspectRatioBox} from './boxes/aspect-ratio/jackpot-widget-aspect-ratio.box';
import {JackpotWidgetAmountContainer} from './boxes/amount/jackpot-widget-amount-container.box';
import {JackpotWidgetClockLabelSkeletonBox} from "./skeleton/boxes/footer/jackpot-widget-clock-label-skeleton.box";
import {JackpotWidgetCollectingLabelSkeletonBox} from "./skeleton/boxes/footer/jackpot-widget-collecting-label-skeleton.box";
import {JackpotWidgetFooterSkeletonBox} from "./skeleton/boxes/footer/jackpot-widget-footer-container-skeleton.box";
import {JackpotWidgetAmountSkeletonBox} from "./skeleton/boxes/jackpot-widget-amount-skeleton.box";
import {action, observable} from "mobx";
import { imagePreload } from '../../../common/utils/image-preload';
import { PartyJackpotJoinStatus } from '../../../common/interfaces/blue-ribbon-party-jackpot.interface';
import {ResizeObserver, ResizeObserverEntry} from '@juggle/resize-observer';
import { IJackpotHandler } from '../../services/jackpots-with-state/jackpot-handler/jackpot-handler.interface';
import { IJackpotData } from '../../services/jackpots-with-state/jackpots-with-state.service.interface';

export interface JackpotWidgetComponentProps {
	widgetConfig?: JackpotWidgetConfiguration;
	/**
	 * We send the wrapper as prop because if the widget is not rendered, then we don't want to render the Wrapper either
	 */
    WrapperBox?: React.ComponentType;
    forcePCView?: boolean;
    shouldMinimize?: boolean;
    seoCategory?: string;
    onClick?: () => void;
}

@observer
export class JackpotWidgetComponent extends OrbitComponent<JackpotWidgetComponentProps> {

	private _onWidgetClick = () => {
        //Product want to check how many users click on the widgets even if it's not clickable
        this.services.analytics.sendAnalytics('Navigation', 'Jackpot_Widget', `${this.services.routing.currentRoute?.data?.arenaType}, ${this.props.widgetConfig?.jackpotType}`);

        if (this.props.onClick) {
            const listInfo = this.services.configuration.getGamesListByJackpotId(this.props.widgetConfig.jackpotId);
            this.services.analytics.sendAnalytics(this.props.seoCategory ?? "List_Widget", "Widget_Clicked", this.props.widgetConfig.jackpotType + (listInfo?.title? "_" + listInfo.title : ""));
            this.props.onClick();
        }
        if (this.props.widgetConfig.jackpotType === JackpotType.Party) {
            return this.services.partyJackpot.openJackpotInfoPage( this.props.widgetConfig?.jackpotId);
        }
    }
    private _isWidgetClickable = () => {
        return !!this.props.onClick || this.props.widgetConfig.jackpotType === JackpotType.Party;
    }

    @observable
    private imagesReady = false;

    @action
    private _setImagesReady(readyState: boolean) {
        this.imagesReady = readyState;
    }

    private get _isJackpotHandlerReady(): boolean {
        const jackpotId = this.props.widgetConfig?.jackpotId ?? JackpotInternalId.Daily;
        const jackpotHandler = this.services.jackpotsWithState.currentJackpots[jackpotId];
        return !Check.isNullOrUndefined(jackpotHandler?.jackpotData);
    }

    private get _shouldRenderSkeleton() {
        return (!this.imagesReady || !this._isJackpotHandlerReady);
    }

    componentDidMount() {
        super.componentDidMount();

        const jackpotType = this.props.widgetConfig?.jackpotType ?? JackpotType.Daily;

        if (!this.services.theme.currentTheme.images.jackpotWidget.byType[jackpotType]) {
            // this can happen if in Orbit Admin metadata has jackpotType has unknown value for Orbit - for example "HourlyCaptain8"
            this.services.logger.warning(`Unknown JP type ${jackpotType} found!`)
            return;
        }

        Promise.all([
            imagePreload(this.services.theme.currentTheme.images.jackpotWidget.byType[jackpotType].logo.toString()),
            imagePreload(this.services.theme.currentTheme.images.jackpotWidget.byType[jackpotType].background.toString())
        ]).then(() => {
            this._setImagesReady(true);
        });
    }

    componentWillUnmount() {
        if (this.resizeObserverInstance) {
            this.resizeObserverInstance.disconnect();
            this.resizeObserverInstance = null;
        }
    }

    @observable
    private widgetWidth: number;
    private aspectRatioBox: HTMLDivElement | null;
    private resizeObserverInstance: ResizeObserver;
    private disposer: undefined | ((oldRefEl: HTMLDivElement) => void);

    @action
    private setWidgetWidth = (entries: ResizeObserverEntry[]) => {
        // We know we are only watching the aspect ratio box element so we take the first element in the array.
        // We are not using this.aspectRatioBox directly because from a typescript point of view that could be null
        //  and from a react component lifecycle perspective it could be null as well
        let targetElement = entries[0].target;
        const elementRectangle = targetElement.getBoundingClientRect();
        this.widgetWidth = elementRectangle.width;
    };

    private _captureJackpotWidgetAspectRatioBoxRef = (refEl: HTMLDivElement | null) => {
        if (this.disposer) {
            this.disposer(this.aspectRatioBox);
            this.disposer = undefined;
        }
        this.aspectRatioBox = refEl;
        if (this.aspectRatioBox) {
            if (!this.resizeObserverInstance) {
                this.resizeObserverInstance = new ResizeObserver(this.setWidgetWidth);
            }
            this.resizeObserverInstance.observe(this.aspectRatioBox);
            this.disposer = (oldRefEl: HTMLDivElement) => {
                // At unmount, the resizeObserverInstance will be destroyed and the disposer will be called too late.
                // In that case, we don't do anything since the observer was disconnected at unmount
                if (this.resizeObserverInstance) {
                    this.resizeObserverInstance.unobserve(oldRefEl);
                }
            }
        }
    };

    private _getCountDownTimerTranslationKey(jackpotState: JACKPOT_STATE) {
        let translationKey = '';
        switch (jackpotState) {
            case JACKPOT_STATE.Hot:
                translationKey = 'JACKPOT-WIDGET-DROPS-SOON';
                break;

            case JACKPOT_STATE.OffHours:
                translationKey = 'JACKPOT-WIDGET-STARTS-SOON';
                break;

            case JACKPOT_STATE.Regular:
            default:
                translationKey = 'JACKPOT-WIDGET-MUST-PAY';
                break;
        }

        return translationKey;
    }

    render() {
		if (!this.props.widgetConfig || !this.props.widgetConfig.display) {
			return null;
		}

        if (this.props.widgetConfig.jackpotType === JackpotType.Party) {
            const jackpotInfo = this.services.partyJackpot.getPartyJackpot(this.props.widgetConfig.jackpotId);
            //Party jackpot list should not be showed if jackpot state is not active or user declined to play
            if (!jackpotInfo ||
                (jackpotInfo && (jackpotInfo.isExpired || jackpotInfo.joinStatus === PartyJackpotJoinStatus.NotJoined))) {
                return null;
            }
        }

		const jackpotId = String(this.props.widgetConfig.jackpotId ?? JackpotInternalId.Daily);
		const jackpotType: JackpotType = this.props.widgetConfig.jackpotType ?? JackpotType.Daily;

		const jackpotHandler: IJackpotHandler = this.services.jackpotsWithState.currentJackpots[jackpotId];
        const jackpotData = (jackpotHandler?.jackpotData || {}) as IJackpotData;

        if(!this._shouldRenderSkeleton) {
            jackpotHandler.startGradual(jackpotData);
        }

        const {jackpotState, lastWinAmount} = jackpotData || {};

		const WrapperBox = this.props.WrapperBox || React.Fragment;
		const forcePCView = this.props.forcePCView || false;
		const shouldMinimize = this.props.shouldMinimize || false;

        const themeType = this.services.jackpotsWithState.getThemeType(jackpotType);

        let mustHitTimeFn: () => Date = jackpotData ? () => {
            return jackpotData.mustHitTime;
        } : () => new Date(0);

        return <WrapperBox>
            <JackpotWidgetAspectRatioBox forcePCView={forcePCView} shouldMinimize={shouldMinimize} ref={this._captureJackpotWidgetAspectRatioBoxRef}>
                <JackpotWidgetBox onClick={this._onWidgetClick} isClickable={this._isWidgetClickable()} className="cy-jackpot-widget">
                    <JackpotWidgetBgBox shouldRenderSkeleton={this._shouldRenderSkeleton} jackpotType={jackpotType} widgetIsMinimized={shouldMinimize}>
                        {jackpotState === JACKPOT_STATE.Hot ? <>
                            <JackpotWidgetBorderShine/>
                            <JackpotWidgetRadialShine/>
                            <JackpotWidgetBorderShine bottom/>
                            <JackpotWidgetRadialShine bottom/>
                        </> : null}

                        {(this._shouldRenderSkeleton) ?
                            <>
                                <JackpotWidgetAmountSkeletonBox />
                                <JackpotWidgetFooterSkeletonBox>
                                    <JackpotWidgetCollectingLabelSkeletonBox />
                                    <JackpotWidgetClockLabelSkeletonBox />
                                </JackpotWidgetFooterSkeletonBox>
                            </> :
                            <>
                                <JackpotWidgetLogoBox
                                    src={this.services.theme.currentTheme.images.jackpotWidget.byType[jackpotType].logo.toString()}
                                    alt="jackpot-logo"
                                    forcePCView={forcePCView}
                                    widgetIsMinimized={shouldMinimize}
                                />
                                <JackpotWidgetAmountContainer widgetIsMinimized={shouldMinimize}>
                                    {jackpotState === JACKPOT_STATE.Cold &&
                                        <JackpotWidgetAmountLabelBox forcePCView={forcePCView} widgetIsMinimized={shouldMinimize} widgetWidth={this.widgetWidth} themeType={themeType}>
                                            {this.services.translation.translate('JACKPOT-WIDGET-LAST-WIN')}
                                        </JackpotWidgetAmountLabelBox>
                                    }

                                    <JackpotWidgetAmountBox forcePCView={forcePCView} state={jackpotState} widgetIsMinimized={shouldMinimize} widgetWidth={this.widgetWidth} themeType={themeType}>
                                        {jackpotState === JACKPOT_STATE.Cold ?
                                            this.services.currency.formatMoney(lastWinAmount) :
                                            this.services.currency.formatMoney(jackpotHandler.jackpotAmount)}
                                    </JackpotWidgetAmountBox>
                                </JackpotWidgetAmountContainer>

                                <JackpotWidgetFooterBox forcePCView={forcePCView} widgetIsMinimized={shouldMinimize}>
                                    {jackpotState === JACKPOT_STATE.Cold ?
                                        <>
                                            <JackpotWidgetCollectingLabelBox forcePCView={forcePCView} widgetIsMinimized={shouldMinimize} widgetWidth={this.widgetWidth} themeType={themeType}>
                                                {this.services.translation.translate('JACKPOT-WIDGET-NEXT-JACKPOT')}
                                            </JackpotWidgetCollectingLabelBox>

                                            <JackpotWidgetCollectingAmountBox forcePCView={forcePCView} widgetIsMinimized={shouldMinimize} widgetWidth={this.widgetWidth} themeType={themeType}>
                                                {this.services.currency.formatMoney(jackpotHandler.jackpotAmount)}
                                            </JackpotWidgetCollectingAmountBox>
                                        </> :
                                        <>
                                            <JackpotWidgetClockLabelBox state={jackpotState} forcePCView={forcePCView} widgetIsMinimized={shouldMinimize} widgetWidth={this.widgetWidth} themeType={themeType}>
                                                {this.services.translation.translate(this._getCountDownTimerTranslationKey(jackpotState))}
                                            </JackpotWidgetClockLabelBox>

                                            <JackpotWidgetClockComponent
                                                jackpotState={jackpotState}
                                                getMustHitTime={mustHitTimeFn}
                                                forcePCView={forcePCView}
                                                widgetIsMinimized={shouldMinimize}
                                                widgetWidth={this.widgetWidth}
                                                jackpotType={jackpotType}
                                            />
                                        </>}
                                </JackpotWidgetFooterBox>
                            </>}
                    </JackpotWidgetBgBox>
                </JackpotWidgetBox>
            </JackpotWidgetAspectRatioBox>
        </WrapperBox>;
	}

}
